import { useState, useCallback, useMemo, useEffect } from 'react';
import { Box } from '@primerai/thread';
import { useSelector, useDispatch } from 'react-redux';
import {
  FormInputsWrapper,
  SignInForm,
  SubmitButton,
  FadeInPasswordField,
} from './Form.styles';
import {
  useLoginState,
  useSSOLogin,
  useUsernamePasswordLogin,
} from './helpers';
import TermsAcknowledgement from './TermsAcknowledgement';
import { FormProps } from './types';
import { TextInput, PasswordInput } from '~/components/form/input';
import { Actions } from '~/store/login/actions';
import { getLoginUsername } from '~/store/login/selectors';

const Form = ({ isSSOPrompt }: FormProps) => {
  const dispatch = useDispatch();
  const username = useSelector(getLoginUsername);
  const [password, setPassword] = useState('');
  const { loading: isLoadingLogin, error, success, url } = useLoginState();
  const handleSSOLogin = useSSOLogin();
  const handleUsernamePassLogin = useUsernamePasswordLogin();

  const isRedirectingToSAMLProvider = useMemo<boolean>(
    (): boolean => success && url,
    [success, url],
  );

  const submitIsLoadingText = useMemo((): string => {
    if (isRedirectingToSAMLProvider) {
      return 'Redirecting to SAML provider...';
    }

    if (!isSSOPrompt) {
      return 'Authenticating...';
    }

    return 'Authenticating...';
  }, [isRedirectingToSAMLProvider, isSSOPrompt]);

  const setUsername = useCallback((usernameValue: string): void => {
    dispatch(Actions.SET_USERNAME(usernameValue));
  }, []);

  const checkUsernameToSSO = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isUsernameValid = username.trim().length;
      if (isUsernameValid) {
        e.preventDefault();
        handleSSOLogin(username, true);
      }
    },
    [handleSSOLogin],
  );

  const onSubmit = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isSSOPrompt) {
        handleSSOLogin(username);
      } else {
        handleUsernamePassLogin(username, password);
      }
    },
    [handleSSOLogin, handleUsernamePassLogin],
  );

  const updateUsername = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUsername(e.target.value);

  const updatePassword = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPassword(e.target.value);

  useEffect(() => {
    setUsername('');
  }, []);

  return (
    <SignInForm onSubmit={onSubmit}>
      <FormInputsWrapper>
        <TextInput
          autoFocus
          disabled={isLoadingLogin}
          error={isSSOPrompt ? error : undefined}
          onBlur={checkUsernameToSSO}
          onChange={updateUsername}
          placeholder="Username or email address"
          value={username}
        />
        <FadeInPasswordField className={!isSSOPrompt ? 'fadeIn' : undefined}>
          {!isSSOPrompt && (
            <PasswordInput
              autocomplete="current-password"
              autoFocus
              error={error}
              onChange={updatePassword}
              placeholder="Password"
              value={password}
            />
          )}
        </FadeInPasswordField>
      </FormInputsWrapper>
      <TermsAcknowledgement />

      <Box mt="$6">
        <SubmitButton
          appearance="primary"
          disabled={isLoadingLogin}
          height={55}
          intent="primary"
          isLoading={isLoadingLogin || isRedirectingToSAMLProvider}
          loadingText={submitIsLoadingText}
          type="submit"
        >
          Continue
        </SubmitButton>
      </Box>
    </SignInForm>
  );
};

export default Form;
